import React, { useMemo } from 'react';
import ImpressionTimeline from '../../../../../../shared/designSystem/components/ImpressionTimeline';
import { getMobileOperatingSystem, determineAdServer } from './utils';

// =============================================================================
// ImpressionsTimeLine (V1 SignUps) — facelifted off the local AntD `Timeline`
// + `Modal` chrome to the shared `<ImpressionTimeline/>` primitive (already
// in use on V2 SignUps + V2 Transactions + V1 Transactions expanded rows).
//
// The macro-resolution rules from the legacy implementation are preserved
// verbatim — they live in `normalizeImpression` below as a pure
// transformation step. All ad-server detection, app-store / sitename
// fallback chains, and OS classification are unchanged. Only the rendering
// chrome moves to the shared primitive, which gives this view:
//   • mono timestamps
//   • publisher block (icon + name + Visit link)
//   • Platform meta row (Lucide icon)
//   • Media row — the legacy "Served on …" / "Referred by …" descriptor
//   • clickable creative thumbnail (right side) → Modal preview
//
// Both `showMediaRow` (text descriptor) and `showCreativeThumb` (image)
// are enabled — V1 legacy had both, the primitive split lets us keep both
// without conflict. `media` carries the descriptor; `creativeUrl` carries
// the image URL.
// =============================================================================

const safeDecode = (s) => {
  try {
    return decodeURIComponent(s);
  } catch (e) {
    return s;
  }
};

// Pure transformation — V1 raw impression row → primitive's expected shape.
// Logic copied byte-for-byte from the legacy `makeTimeLine` mapper.
const normalizeImpression = (impression, i) => {
  const {
    IMPRESSION_TIME,
    IMPRESSION_USERAGENT,
    IMPRESSION_PAGE_URL,
    PAGE_URLHOST,
    IMPRESSION_REFERRER,
    IMPRESSION_PUBLISHERNAME,
    IMPRESSION_APPNAME,
    IMPRESSION_APPSTORE_ID,
    IMPRESSION_SITE_ID,
    IMPRESSION_SITENAME,
    CREATIVEURL,
    CREATIVEID,
  } = impression;

  const OS = IMPRESSION_USERAGENT ? getMobileOperatingSystem(IMPRESSION_USERAGENT) : 'Unknown';

  let appSiteName = '';
  let appSiteLink = '';
  let applicationPlatform = '';

  // First case — none of the new app/site macros resolve. Fall back to
  // page URL / referrer for ad-server detection (TTD/CENTRO/LiquidM legacy
  // tags). Behavior preserved verbatim from V1.
  if (
    (!IMPRESSION_APPSTORE_ID || IMPRESSION_APPSTORE_ID === 'N/A') &&
    (!IMPRESSION_APPNAME || IMPRESSION_APPNAME === 'N/A') &&
    (!IMPRESSION_SITE_ID || IMPRESSION_SITE_ID === 'N/A') &&
    (!IMPRESSION_SITENAME || IMPRESSION_SITENAME === 'N/A')
  ) {
    appSiteName = PAGE_URLHOST ? `Served on ${safeDecode(PAGE_URLHOST)}` : '';
    appSiteLink = IMPRESSION_REFERRER
      ? `Referred by ${safeDecode(IMPRESSION_REFERRER).substring(0, 60)}`
      : '';
    applicationPlatform = PAGE_URLHOST ? determineAdServer(PAGE_URLHOST) : '';
    if (IMPRESSION_REFERRER && applicationPlatform !== 'Application') {
      applicationPlatform = determineAdServer(IMPRESSION_REFERRER);
    }
  } else if (IMPRESSION_APPSTORE_ID && IMPRESSION_APPNAME) {
    // Mobile application — high confidence via new macros.
    appSiteName = `Served on - ${safeDecode(IMPRESSION_APPNAME)
      .replace(/[+_]/g, ' ')
      .split(':')
      .pop()}`;
    appSiteLink = safeDecode(IMPRESSION_APPSTORE_ID);
    applicationPlatform = 'Application';
  } else if (IMPRESSION_SITENAME && IMPRESSION_SITE_ID && !IMPRESSION_SITE_ID) {
    // Mobile application — site name without site id.
    appSiteName = `Served on - ${safeDecode(IMPRESSION_SITENAME)
      .replace(/[+_]/g, ' ')
      .split(':')
      .pop()}`;
    appSiteLink = IMPRESSION_REFERRER
      ? `Referred by ${IMPRESSION_REFERRER.substring(0, 30)}`
      : '';
    applicationPlatform = 'Application';
  } else if (IMPRESSION_SITENAME && IMPRESSION_APPNAME) {
    // Website — better resolution than page-level information.
    appSiteName = `Served on - ${safeDecode(IMPRESSION_SITENAME)
      .replace(/[+_]/g, ' ')
      .split(':')
      .pop()}`;
    applicationPlatform = 'Website';
  }

  // Platform string the primitive renders + uses for the icon. Prefer the
  // device-OS classification (drives the Smartphone/Tablet/Monitor icon
  // automatically via the primitive's default `platformIconFor`); fall back
  // to the publisher-side platform when OS is unknown.
  const platform =
    OS && OS !== 'Unknown' && OS !== 'Computer' ? OS : applicationPlatform || OS;

  // Publisher name = the resolved "Served on -..." site/app name when
  // available. `IMPRESSION_PUBLISHERNAME` (when meaningful) becomes the
  // separate "Published by" attribution line. When site/app data isn't
  // resolved, fall back to PUBLISHERNAME as the publisher itself.
  const decodedPublisherName =
    IMPRESSION_PUBLISHERNAME && IMPRESSION_PUBLISHERNAME !== 'N/A'
      ? safeDecode(IMPRESSION_PUBLISHERNAME).replace(/[+_]/g, ' ')
      : null;
  let publisher;
  let publishedBy;
  let mediaDescriptor = appSiteName;
  if (appSiteName) {
    publisher = appSiteName.replace(/^Served on\s*-?\s*/i, '');
    mediaDescriptor = '';
    publishedBy = decodedPublisherName || undefined;
  } else if (decodedPublisherName) {
    publisher = decodedPublisherName;
  } else {
    publisher = 'Unknown publisher';
  }

  return {
    eventId: CREATIVEID || `imp-${i}`,
    impressionTime: IMPRESSION_TIME,
    impressionPublisher: publisher,
    impressionPublisherUrl: appSiteLink || '',
    impressionPublisherPlatform: platform,
    platformTooltip: IMPRESSION_USERAGENT || platform,
    creativeUrl: CREATIVEURL || undefined,
    media: mediaDescriptor || '',
    applicationPlatform: applicationPlatform || undefined,
    publishedBy,
  };
};

const ImpressionSubTable = ({ impressions = [] }) => {
  const normalized = useMemo(
    () => (impressions || []).map(normalizeImpression),
    [impressions]
  );

  return (
    <ImpressionTimeline
      impressions={normalized}
      sort="newest"
      showMediaRow
      showCreativeThumb
    />
  );
};

export default ImpressionSubTable;
